import { Component, OnInit, Input } from '@angular/core';
import { Expert, ExpertProvinces, ExpertServices, ExpertTeamLeaderProvinces, SalaryFrequency, SalaryFrequencyAux, TeamLeaderPartners } from '../../models/expert.schema';
import { Router, ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ExpertService } from '../../services/expert.service';
import { AbstractControl, FormArray, FormGroup, Validators } from '@angular/forms';
import { FormGroupToObject } from '../../pipes/form-group-to-object.pipe';
import { Vehicle } from '../../models/vehicle.schema';
import { orderByProperty } from '../../helpers/order-by-property.helper';
import { User } from '../../models/user.schema';
import { AuthStatusService } from '../../services/auth-status.service';
import { UserService } from '../../services/user.service';
import { ExpertServiceService } from 'src/app/services/expert-service.service';
import { ExpertServiceSchema } from 'src/app/models/expert-service.schems';
import { ProvincesService } from 'src/app/services/provinces.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { DatePipe } from '@angular/common';
import { TransactionsService } from 'src/app/services/transactions.service';
import { PartnerService } from 'src/app/services/partner.service';
import { Partner } from 'src/app/models/partner.schema';
import { APP_CONSTANTS } from 'src/app/constants/constants';
import { HttpEventType } from '@angular/common/http';

@Component({
  selector: 'app-expert-form',
  templateUrl: './expert-form.component.html',
  styleUrls: ['./expert-form.component.scss']
})
export class ExpertFormComponent implements OnInit {
  @Input('profile') public profile: boolean = false;
  public readonly formValidationMsgs;
  public _expertForm: any;
  public expert: Expert;
  public vehicles: Vehicle[];

  private checkedPassword1: boolean;
  private checkedPassword2: boolean;
  public tmpBtnDisabled: boolean;
  public groups: string[];
  public services: ExpertServiceSchema[] = [];
  selectedServices: any[] = [];
  showExtraKmInput: boolean[] = [];
  showSalaryPerItem: boolean[] = [];
  provinces: any[] = [];
  salaryFrequency = SalaryFrequency;
  salaryFrequencyAux = SalaryFrequencyAux;
  isEligibleForWeeklyPayment: boolean = false;
  totalCreditAmount: number;
  partners: Partner[] = [];
  isDragging = false;
  uploadedFile: File | null = null;
  isUploading = false;
  uploadProgress = 0;
  errorMessage = '';
  private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 10MB
  private readonly ALLOWED_TYPE = 'application/pdf';
  availableForUploadContract: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private _snackBar: MatSnackBar,
    private expertService: ExpertService,
    private userService: UserService,
    private authStatusService: AuthStatusService,
    private _expertServiceService: ExpertServiceService,
    private provincesService: ProvincesService,
    private spinnerService: SpinnerService,
    private transactionService: TransactionsService,
    private partnerService: PartnerService,
  ) {
    this.formValidationMsgs = Expert.validationMessages;
    try {
      this.groups = this.authStatusService.getRoles().map((elem: string) => elem.toLowerCase());
    }
    catch {
      this.groups = [];
    }
  }

  async ngOnInit() {
    try {
      this.spinnerService.show();
      this.services = await this._expertServiceService.getAllServicess();
      this.provinces = await this.provincesService.getAllProvinces();
      this.partners = await this.partnerService.getAllActivePartnersForDelivery().then(res => res.partners);
      await this.initialize();

      this._expertForm.get('isVatCode').valueChanges.subscribe((isVat: boolean) => {
        const vatCodeCtrl = this._expertForm.get('vatCode');
        const fiscalCodeCtrl = this._expertForm.get('fiscalCode');

        if (isVat) {
          vatCodeCtrl.setValidators([
            Validators.required,
            Validators.pattern(/^\d{11}$/)
          ]);
          fiscalCodeCtrl.clearValidators();

          vatCodeCtrl.enable();
          fiscalCodeCtrl.disable();
          fiscalCodeCtrl.reset();
        } else {
          fiscalCodeCtrl.setValidators([
            Validators.required,
            Validators.pattern(/^\w{6}\d{2}\w\d{2}\w\d{3}\w$/)
          ]);
          vatCodeCtrl.clearValidators();

          fiscalCodeCtrl.enable();
          vatCodeCtrl.disable();
          vatCodeCtrl.reset();
        }

        vatCodeCtrl.updateValueAndValidity();
        fiscalCodeCtrl.updateValueAndValidity();
      });
      const isVatInitial = this._expertForm.get('isVatCode').value;
      this._expertForm.get('isVatCode').setValue(isVatInitial);

      this.spinnerService.hide();
    }
    catch(error) { 
      console.log(error);
    }
  }

  async initialize() {
    this.vehicles = orderByProperty(await this.expertService.getAllVehicles(), 'name');
    this._expertForm = new FormGroup(Expert.validation(this.vehicles, this.profile));
    const expertId = this.profile ?
      (await this.expertService.getOne(this.authStatusService.getExtra()['id'])).id.toString() :
      this.route.snapshot.paramMap.get('id');
    if (expertId) {
      try {
        this.expert = await this.expertService.getOne(expertId);
        this.availableForUploadContract = this.expert?.contract?.contractUrl && !this.expert?.contract?.signedContractUrl ? true : false;
      }
      catch {
        this.router.navigateByUrl("/404", { skipLocationChange: true });
      }
    }

    // Getting the total credit amount
    let creditAmount: number  = (await this.transactionService.getCreditAmount(Number(expertId))).amount || 0;
    this.totalCreditAmount = creditAmount;
    this.checkSalaryFrequency();

    if (!this.expert) {
      this.expert = new Expert();
      this.expert.user = this.route.snapshot.paramMap.get('userId') ?
        await this.userService.getOne(this.route.snapshot.paramMap.get('userId')) :
        new User();
    }
    this.patchAdditionalInfo(expertId);
    this.patch();
    if (this.profile)
      this.setProfileValidators();
  }

  patchAdditionalInfo(expertId: number|string){
    if(expertId && this.expert?.expertServices?.length > 0){
      this.patchExpertServices();
    }
    if(expertId && this.expert?.expertProvinces?.length > 0){
      this.patchExpertProvinces();
    }
    if(expertId && this.expert?.expertTeamLeaderProvinces?.length > 0){
      this.patchExpertTeamLeaderProvinces();
    }
    if(expertId && this.expert?.teamLeaderPartners?.length > 0){
      this.patchTeamLeaderPartners();
    }
  }

  patch() {
    [
      'phone', 
      'address', 
      'notes', 
      'longitude', 
      'latitude', 
      'minimumKmIncluded', 
      'isTeamLeader', 
      'receiveWhatsappMsg', 
      'receiveEmailMsg', 
      'extraOutSideCityKmPrice',
      'isVatCode',
      'vatCode',
      'fiscalCode',
      'dateOfBirth',
      'birthPlace',
      'bankAccountData',
      'holdingPercentage',
      'salaryFrequency',
      'weeklyDepositLimit',
      // 'expertIndentityCode'
    ].forEach(param => {
      if (this.expert[param] instanceof Date)
        this._expertForm.controls[param].setValue(new DatePipe('it-IT').transform(this.expert[param], 'yyyy-MM-dd'));
      this._expertForm.controls[param].setValue(this.expert[param]);
    });
    [
      'surname', 
      'name', 
      'email'
    ].forEach(param => {
      this._expertForm.controls.user.controls[param].setValue(this.expert.user[param]);
    });
    if (this.expert.vehicles){
      for (let i = 0; i < this.vehicles.length; i++) {
        for (const vehicle of this.expert.vehicles)
          if (vehicle.id === this.vehicles[i].id) {
            this._expertForm.controls.vehicles.controls[i].setValue(true);
            break;
          }
      }
    }
  }

  patchExpertServices(){
    const control = <FormArray>this._expertForm.get('expertServices');
    control.clear();
    if (!this.expert.expertServices || Array.isArray(this.expert.expertServices) && this.expert.expertServices.length == 0){
      this.expert.expertServices = [new ExpertServices()];
    }
    this.expert.expertServices.forEach((f, index) => {
      control.push(this.patchValues(f, index));
    });
    this.selectedServices = this._expertForm.controls.expertServices?.value;
  }

  
  patchValues(expertServices: ExpertServices, index: number) {
    this.showExtraKmInput[index] = expertServices?.service?.serviceType?.toLowerCase() === APP_CONSTANTS.FIXED_SERVICE_SALARY_MODEL || expertServices?.service?.serviceType?.toLowerCase() === APP_CONSTANTS.WAREHOUSE_SERVICE_SALARY_MODEL;
    this.showSalaryPerItem[index] = expertServices?.service?.serviceType?.toLowerCase() === APP_CONSTANTS.WAREHOUSE_SERVICE_SALARY_MODEL;
    let serviceForm = Expert.serviceFormGroup();
    serviceForm.controls.service.setValue(
      { 
        id: expertServices?.service?.id, 
        salary: expertServices?.salary,
        minimumKmPrice: expertServices?.minimumKmPrice,
        salaryPerItem: expertServices?.salaryPerItem,
        // extraOutSideCityKmPrice: expertServices?.extraOutSideCityKmPrice
      }
    );
        
    return serviceForm;
  }

  
  patchExpertProvinces(){
    const control = <FormArray>this._expertForm.get('expertProvinces');
    control.clear();
    if (!this.expert.expertProvinces || Array.isArray(this.expert.expertProvinces) && this.expert.expertProvinces.length == 0){
      this.expert.expertProvinces = [new ExpertProvinces()];
    }
    this.expert.expertProvinces.forEach((f, index) => {
      control.push(this.patchExpertProvinceValues(f, index));
    });
  }

  patchExpertProvinceValues(expertProvinces: ExpertProvinces, index: number) {
    let provinceForm = Expert.provinceFormGroup();
    provinceForm.controls.province.setValue(
      { 
        id: expertProvinces?.province?.id, 
      }
    );
        
    return provinceForm;
  }

  patchExpertTeamLeaderProvinces(){
    const control = <FormArray>this._expertForm.get('expertTeamLeaderProvinces');
    control.clear();
    if (!this.expert.expertTeamLeaderProvinces || Array.isArray(this.expert.expertTeamLeaderProvinces) && this.expert.expertTeamLeaderProvinces.length == 0){
      this.expert.expertTeamLeaderProvinces = [new ExpertTeamLeaderProvinces()];
    }
    this.expert.expertTeamLeaderProvinces.forEach((f, index) => {
      control.push(this.patchExpertTeamLeaderProvinceValues(f, index));
    });
  }

  patchExpertTeamLeaderProvinceValues(expertTeamLeaderProvinces: ExpertTeamLeaderProvinces, index: number) {
    let provinceForm = Expert.teamLeaderProvinceFormGroup();
    provinceForm.controls.province.setValue(
      { 
        id: expertTeamLeaderProvinces?.province?.id, 
      }
    );
        
    return provinceForm;
  }

  patchTeamLeaderPartners(){
    const control = <FormArray>this._expertForm.get('teamLeaderPartners');
    control.clear();
    if (!this.expert.teamLeaderPartners || Array.isArray(this.expert.teamLeaderPartners) && this.expert.teamLeaderPartners.length == 0){
      this.expert.teamLeaderPartners = [new TeamLeaderPartners()];
    }
    this.expert.teamLeaderPartners.forEach((f, index) => {
      control.push(this.patchTeamLeaderPartnerValues(f, index));
    });
  }

  patchTeamLeaderPartnerValues(teamLeaderPartner: TeamLeaderPartners, index: number) {
    console.log("teamLeaderPartner", teamLeaderPartner)
    let provinceForm = Expert.teamLeaderPartnerFormGroup();
    provinceForm.controls.partner.setValue(
      { 
        id: teamLeaderPartner?.partner?.id, 
      }
    );
        
    return provinceForm;
  }
  setProfileValidators() {
    this.checkedPassword1 = false;
    this.checkedPassword2 = false;

    this._expertForm.controls.user.controls.password1.valueChanges.subscribe((next: any) => {
      if (!this.checkedPassword1) {
        this.checkedPassword1 = true;
        this._expertForm.controls.user.controls.password2.updateValueAndValidity();
      }
      else {
        this.checkedPassword1 = false;
        this.checkedPassword2 = false;
      }
    });

    this._expertForm.controls.user.controls.password2.valueChanges.subscribe((next: any) => {
      if (!this.checkedPassword2) {
        this.checkedPassword2 = true;
        this._expertForm.controls.user.controls.password1.updateValueAndValidity();
      }
      else {
        this.checkedPassword1 = false;
        this.checkedPassword2 = false;
      }
    });
  }

  onSubmit() {
    this.tmpBtnDisabled = true;
    let expert: Expert = (new FormGroupToObject()).transform(this._expertForm);
    expert['vehicles'] = [];
    for (let i = 0; i < this._expertForm.controls.vehicles.controls.length; i++) {
      if (this._expertForm.controls.vehicles.controls[i].value)
        expert['vehicles'].push(this.vehicles[i]);
    }
    let promiseResult: any;
    let createType: boolean = true;
    if (this.profile &&
      this._expertForm.controls.user.value.password1 &&
      this._expertForm.controls.user.value.password1 === this._expertForm.controls.user.value.password2
    ){
      expert['user']['password'] = this._expertForm.controls.user.value.password1;
    }

    if(this.groups.includes('expert')){
      delete expert?.expertProvinces;
      delete expert?.expertServices;
      delete expert?.expertTeamLeaderProvinces;
    }

    if(this.groups.includes('admin') || this.groups.includes('operation')){
      if(expert?.expertProvinces[0]?.province?.id === null || expert?.expertProvinces[0]?.province?.id === undefined){
        delete expert.expertProvinces;
      }
      if(expert?.expertServices[0]?.service?.id === null || expert?.expertServices[0]?.service?.id === undefined){
        delete expert.expertServices;
      }
      if(expert?.expertTeamLeaderProvinces[0]?.province?.id === null || expert?.expertTeamLeaderProvinces[0]?.province?.id === undefined){
        delete expert.expertTeamLeaderProvinces;
      }
    }
    // if(this.groups.includes('operation')){
    //   expert.expertServices = null;
    // }
    // console.log('expert', expert);
    // return;
    if (this.expert['id']) {
      expert['user']['id'] = this.expert.user.id;
      promiseResult = this.profile ?
        this.expertService.updateMe(expert) :
        this.expertService.update(this.expert['id'], expert);
      createType = false;
    }
    else {
      if (this.expert.user.id)
        expert['user']['id'] = this.expert.user.id;
      promiseResult = this.expertService.create(expert);
    }
    promiseResult.then((data: Expert) => {
      this.expert = data;
      const message: string = `Expert ${createType ? "creato" : "aggiornato"} con successo`;
      this._snackBar.open(message, 'Chiudi', {
        direction: "ltr",
        duration: 2000,
        horizontalPosition: "center",
        politeness: "assertive",
        verticalPosition: "top"
      }).afterDismissed().subscribe(result => {
        this.authStatusService.isAuthenticated().then(valid => {
          if (!valid)
            this.router.navigate(['/']);
        });
        if (createType) {
          if (this.route.snapshot.paramMap.get('userId')){
            this.router.navigate(['/utenti']);
          } else{
            this.router.navigate(['/expert', this.expert.id, 'edit']);
          }
        }
      });
      this.patch();
      this.patchAdditionalInfo(this.expert?.id);
      // this.patchExpertServices();
      this.tmpBtnDisabled = false;
    }).catch(error => {
      let errorMsg = `Errore ${createType ? "nella creazione" : "nell'aggiornamento"} dell'expert`;
      const apiMessage: unknown = error?.error?.message ?? error?.message;
      if (typeof apiMessage === 'string' && apiMessage.startsWith('NEW_EXPERT.DUPLICATE_FOR')) {
        const email = apiMessage.split(':').slice(1).join(':').trim();
        errorMsg = email
          ? `L'email ${email} risulta già associata a un esperto.`
          : `L'email risulta già associata a un esperto.`;
      }

      this._snackBar.open(errorMsg, 'Chiudi', {
        direction: "ltr",
        duration: 2000,
        horizontalPosition: "center",
        politeness: "assertive",
        verticalPosition: "top"
      });
      this.tmpBtnDisabled = false;
      console.log(error);
    });
  }

  getAddress(place: any) {
    if (!this._expertForm) return;
    this._expertForm.controls.address.touched = true;
    this._expertForm.controls.address.setValue(place.formatted_address || place.name || '');
    this._expertForm.controls.latitude.setValue(place.geometry?.location?.lat() || '');
    this._expertForm.controls.longitude.setValue(place.geometry?.location?.lng() || '');
  }

  directions(event: Event) {
    event.preventDefault();
    window.open(`https://www.google.com/maps/dir//${this._expertForm.controls.address.value}`, '_blank');
  }

  addService() {
    const control = <FormArray>this._expertForm.get('expertServices');
    control.push(Expert.serviceFormGroup());
  }


  removeService(index: number) {
    const control = <FormArray>this._expertForm.get('expertServices');
    if (control.length === 1) {
      return;
    }
    control.removeAt(index);
    this.showExtraKmInput.splice(index, 1);
    this.selectedServices = this._expertForm.controls.expertServices?.value;
  }

  /*
  isOptionDisabled(service: any): boolean {
    if(this.selectedServices.length){
      let data = this.selectedServices.map((service) => {
        return this.services.find((item) => {
          return service.service.id === item.id;  
        })?.serviceType;
      });
      return data.includes(service.serviceType) || data.includes(service.serviceType);
    }
  }
  */

  isOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const services of this._expertForm.value.expertServices) {
      if (services.service.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId; 
  }
  
  handleServiceChange(serviceId: any, index: number){
    this.selectedServices = this._expertForm.controls.expertServices?.value;
    const selectedService = this.services.find(service => service.id == serviceId);
    this.showExtraKmInput[index] = selectedService.serviceType.toLowerCase() === APP_CONSTANTS.FIXED_SERVICE_SALARY_MODEL || selectedService.serviceType.toLowerCase() === APP_CONSTANTS.WAREHOUSE_SERVICE_SALARY_MODEL;
    this.showSalaryPerItem[index] = selectedService.serviceType.toLowerCase() === APP_CONSTANTS.WAREHOUSE_SERVICE_SALARY_MODEL;
  }

  addProvince() {
    const control = <FormArray>this._expertForm.get('expertProvinces');
    control.push(Expert.provinceFormGroup());
  }

  isProvinceOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const provinces of this._expertForm.value.expertProvinces) {
      if (provinces.province.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId;
  }

  removeProvince(index: number) {
    const control = <FormArray>this._expertForm.get('expertProvinces');
    if (control.length === 1) {
      return;
    }
    control.removeAt(index);
  }

  addTeamLeaderProvince() {
    const control = <FormArray>this._expertForm.get('expertTeamLeaderProvinces');
    control.push(Expert.teamLeaderProvinceFormGroup());
  }

  removeTeamLeaderProvince(index: number) {
    const control = <FormArray>this._expertForm.get('expertTeamLeaderProvinces');
    if (control.length === 1) {
      return;
    }
    control.removeAt(index);
  }

  isTeamLeaderProvinceOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const provinces of this._expertForm.value.expertTeamLeaderProvinces) {
      if (provinces.province.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId;
  }

  checkSalaryFrequency() {
    if(this.totalCreditAmount > 0 && this.groups.includes('expert') && this.expert) {
      this.isEligibleForWeeklyPayment = Number(this.totalCreditAmount) > Number(this.expert.weeklyDepositLimit);
    }
  }

  changeSalaryFrequency(value: string) {
    try {
      if(this.groups.includes('expert') && this.expert) {
        if(this.isEligibleForWeeklyPayment) {
          return true;
        } else {
          this._expertForm.controls.salaryFrequency.setValue('monthly');
          alert("Non hai diritto al pagamento settimanale. Aumenta l'importo del tuo portafoglio.");
          return false;
        }
      } else {
        return true;
      }
    } catch (error) {
      
    }
  }

  addPartner() {
    const control = <FormArray>this._expertForm.get('teamLeaderPartners');
    control.push(Expert.teamLeaderPartnerFormGroup());
  }

  removePartner(index: number) {
    const control = <FormArray>this._expertForm.get('teamLeaderPartners');
    // if (control.length === 1) {
    //   return;
    // }
    control.removeAt(index);
  }

  isPartnerOptionDisabled(item: any): boolean {
    let hasEmptyId = false;
    for (const partners of this._expertForm.value.teamLeaderPartners) {
      if (partners.partner.id === item.id) {
        hasEmptyId = true;
      }
    }
    return hasEmptyId;
  }

  getErrorMessages(): string[] {
    const errors: string[] = [];

    const scan = (control: AbstractControl, key?: string) => {

      if (!control) return;

      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(k => {
          scan(control.controls[k], k);
        });
        return;
      }

      if (control instanceof FormArray) {
        control.controls.forEach(c => scan(c, key));
        return;
      }

      if (control.errors && key && !errors.includes(key)) {
        errors.push(key);
      }
    };

    scan(this._expertForm);

    return errors;
  }

  translateErrorMessage(error: string): string {
    const messages: { [key: string]: string } = {
      surname: 'COGNOME',
      name: 'NOME',
      email: 'EMAIL',
      phone: 'TELEFONO',
      address: 'INDIRIZZO',

      vatCode: 'PARTITA IVA',
      fiscalCode: 'CODICE FISCALE',

      birthPlace: 'LUOGO DI NASCITA',
      dateOfBirth: 'DATA DI NASCITA',

      bankAccountData: 'IBAN',

      holdingPercentage: 'PERCENTUALE RITENUTA',
      salaryFrequency: 'FREQUENZA SALARIO',
      weeklyDepositLimit: 'LIMITE DEPOSITO SETTIMANALE',

      expertServices: 'SERVIZIO',
      service: 'SERVIZIO',
      salary: 'SALARIO SERVIZIO',
      minimumKmPrice: 'PREZZO KM EXTRA',

      expertProvinces: 'PROVINCIA VALET',
      expertTeamLeaderProvinces: 'PROVINCIA TEAM LEADER',

      extraOutSideCityKmPrice: 'PREZZO EXTRA FUORI CITTÀ'
    };

    return messages[error] || error;
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.handleFile(files[0]);
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.handleFile(input.files[0]);
    }
  }

  private handleFile(file: File): void {

    if(!confirm('Sei sicuro? Questo caricherà il tuo contratto firmato e non sarà possibile annullarlo.')) return;
    
    this.errorMessage = '';

    // Validate file type
    if (file.type !== this.ALLOWED_TYPE && !file.name.toLowerCase().endsWith('.pdf')) {
      this.errorMessage = 'Per favore, carica solo file PDF';
      return;
    }

    // Validate file size
    if (file.size > this.MAX_FILE_SIZE) {
      this.errorMessage = 'Il file supera la dimensione massima di 10MB';
      return;
    }

    // Simulate upload (replace with actual upload logic)
    this.uploadFile(file);
  }

  private uploadFile(file: File): void {
    this.isUploading = true;
    this.uploadProgress = 0;
    this.errorMessage = '';

    this.expertService.uploadContractWithProgress(this.expert.contract.id, file).subscribe(
      (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          this.uploadProgress = Math.round((100 * event.loaded) / (event.total || event.loaded));
        } else if (event.type === HttpEventType.Response) {
          this.isUploading = false;
          this.uploadedFile = file;
          this.initialize();
        }
      },
      (error) => {
        this.isUploading = false;
        this.uploadProgress = 0;
        
        if (error.status === 413) {
          this.errorMessage = 'Il file è troppo grande per essere caricato';
        } else if (error.status === 415) {
          this.errorMessage = 'Tipo di file non supportato';
        } else if (error.status === 0) {
          this.errorMessage = 'Errore di connessione. Verifica la tua rete.';
        } else {
          this.errorMessage = 'Errore durante il caricamento del file. Riprova.';
        }
        
        console.error('Upload error:', error);
      }
    );
  }

  removeFile(): void {
    this.uploadedFile = null;
    this.errorMessage = '';
    this.uploadProgress = 0;
    // Clear form control if needed
    // this.userForm.patchValue({ contractFile: null });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }
}
